<template>
    <div class="single-product-panel-item">
        <div class="image" :style="{ backgroundImage: imageOrPlaceholder(product.image) }"></div>
        <g-level class="title">
            <g-typography tag="h1" font-weight="semibold" :value="product.name"/>
        </g-level>
        <g-level class="price">
            <g-level-item>
                <g-typography v-if="showPrices" tag="h3" font-weight="normal" :value="priceRange"/>
            </g-level-item>
            <g-level-item v-if="outOfStock" slot="right">
                <g-tag color="red"> Out of Stock</g-tag>
            </g-level-item>
        </g-level>
        <div class="variants" v-if="product.variants && product.variants.length > 0">
            <div v-for="option in sortedOptions" :key="option.id" class="variant-option">
                <g-typography tag="div" class="option-label" font-weight="semibold">{{ option.name }}</g-typography>
                <g-select
                    :placeholder="'Select ' + option.name.toLowerCase()"
                    style="width: 100%"
                    size="large"
                    :options="getAvailableOptionsFor(option)"
                    v-model="selectedOptionValues[option.id]"
                    :allow-clear="true"
                />
            </div>
        </div>
        <div v-if="product.requirements" class="description">
            <g-typography v-html="$sanitize(product.requirements)"/>
        </div>
        <g-button
            class="submit-button"
            shape="round"
            theme="live"
            type="primary"
            size="xlarge"
            :disabled="addButtonDisabled"
            block
            @click="handleContinue"
        >
            {{ buttonText }}
        </g-button>
    </div>
</template>

<script>
import ActivationBriefProductConfigMixin from '../../mixins/ActivationBriefProductConfigMixin';
import ProductPriceRangeMixin from '../../mixins/ProductPriceRangeMixin';

export default {
    name: 'NewSingleProductPanelItem',
    props: {
        product: {
            type: Object,
            required: true
        },
        existingValues: {
            type: Array,
            default: () => []
        },
        updateMode: {
            type: Boolean,
            default: false
        },
        panelSlot: {
            type: Object,
            default: () => ({})
        },
        selectedVariant: {
            type: Object,
            default: null
        }
    },
    mixins: [ActivationBriefProductConfigMixin, ProductPriceRangeMixin],
    data() {
        return {
            selectedOptionValues: {},
            optionsCache: new Map()
        };
    },
    created() {
        if (this.existingValues && this.existingValues.length > 0) {
            this.existingValues.forEach(value => {
                this.$set(this.selectedOptionValues, value.id, value.value_id);
            });
        }
    },
    computed: {
        buttonText() {
            return this.updateMode ? 'Update Product' : 'Add Product';
        },
        outOfStock() {
            return this.selectedVariant ? !this.selectedVariant.is_in_stock : !this.product.is_in_stock;
        },
        addButtonDisabled() {
            if (this.outOfStock) return true;
            return this.product.variants?.length > 0 && !this.selectedVariant;
        },
        sortedOptions() {
            return [...this.product.options].sort((a, b) => (a.position || 0) - (b.position || 0));
        },
        selectedVariant() {
            const selections = this.selectedOptionValues;

            const allOptionsSelected = this.product.options.every(option => {
                const hasSelection = option.id in selections && selections[option.id] !== null && selections[option.id] !== undefined;
                return hasSelection;
            });

            if (!allOptionsSelected) {
                return null;
            }

            return this.product.variants.find(variant => {
                return variant.option_values.every(optionValue => {
                    const selectedValue = selections[optionValue.option_id];
                    return String(optionValue.id) === String(selectedValue);
                });
            });
        }
    },
    methods: {
        formatOptionValue(optionValue) {
            return {
                label: optionValue.name,
                value: optionValue.id,
                key: optionValue.id
            }
        },
        getAvailableOptionsFor(targetOption) {
            const cacheKey = `${targetOption.id}:${JSON.stringify(this.selectedOptionValues)}`;
            if (this.optionsCache.has(cacheKey)) {
                return this.optionsCache.get(cacheKey);
            }

            const validValues = new Set(
                this.product.variants
                    .filter(variant => variant.option_values.every(optionValue =>
                        optionValue.option_id === targetOption.id ||
                        !this.selectedOptionValues[optionValue.option_id] ||
                        optionValue.id === this.selectedOptionValues[optionValue.option_id]
                    ))
                    .flatMap(variant => variant.option_values)
                    .filter(ov => ov.option_id === targetOption.id)
                    .map(ov => ov.id)
            );

            const result = targetOption.values
                .filter(value => validValues.has(value.id))
                .map(this.formatOptionValue);

            this.optionsCache.set(cacheKey, result);
            return result;
        },
        getPayload() {
            return {
                id: this.product.id,
                variant_id: this.selectedVariant?.id,
                options: this.selectedVariant?.option_values.map(value => ({
                    id: value.option_id,
                    value_id: value.id,
                    name: value.name
                })) || []
            };
        },
        handleContinue() {
            if (this.addButtonDisabled) return;

            const payload = this.getPayload();
            const eventName = this.updateMode ? 'updated' : 'added';
            const emitData = this.updateMode ? { payload } : { payload, product: this.product };

            this.$emit(eventName, emitData);
        },
    },
};
</script>

<style lang="scss" scoped>
.single-product-panel-item {
    .image {
        width: 100%;
        height: 450px;
        background-size: contain;
        background-repeat: no-repeat;
        background-position: center;
        border: 1px solid $g-color-grey-4;
        border-radius: $g-border-radius;
        margin-bottom: $g-spacing-5;
    }

    .price {
        margin-bottom: $g-spacing-5;
    }

    .description {
        margin-top: $g-spacing-4;
        margin-bottom: $g-spacing-3;
    }

    .variants {
        margin-bottom: $g-spacing-2;

        .variant-option {
            margin-bottom: $g-spacing-3;

            .option-label {
                margin-bottom: $g-spacing-1;
            }
        }

        :deep(.g-select) {
            width: 100%;

            @media screen and (max-width: 768px) {
                .g-select-selector {
                    height: auto;
                    min-height: 44px;
                    padding: 8px 12px;
                }

                .g-select-selection-item {
                    line-height: 1.4;
                    white-space: normal;
                }
            }
        }
    }

    .submit-button {
        margin-top: $g-spacing-2;
        margin-bottom: $g-spacing-6;
    }

    .option-row {
        margin-bottom: $g-spacing-4;
    }
}

:deep(.variant-dropdown) {
    position: fixed !important;
    top: auto !important;
    bottom: 100% !important;
}
</style>
